import { NavLink } from 'react-router-dom'
import { motion, AnimatePresence } from 'framer-motion'
import {
  LayoutDashboard,
  Package,
  ShoppingCart,
  Users,
  Image,
  Ticket,
  Tags,
  Warehouse,
  Settings,
  KeyRound,
  Mail,
  RotateCcw,
  ChevronLeft,
  X,
  Clapperboard,
  Star,
  StickyNote,
  Store,
} from 'lucide-react'
import { Logo } from './Logo'
import { useUiStore } from '@/stores/uiStore'
import { cn } from '@/lib/utils'

const navItems = [
  { to: '/admin', icon: LayoutDashboard, label: 'Dashboard', end: true },
  { to: '/admin/offline-sales', icon: Store, label: 'Offline Sales' },
  { to: '/admin/products', icon: Package, label: 'Products' },
  { to: '/admin/categories', icon: Tags, label: 'Categories' },
  { to: '/admin/notes', icon: StickyNote, label: 'Notes' },
  { to: '/admin/orders', icon: ShoppingCart, label: 'Orders' },
  { to: '/admin/customers', icon: Users, label: 'Customers' },
  { to: '/admin/banners', icon: Image, label: 'Banners' },
  { to: '/admin/instagram-reels', icon: Clapperboard, label: 'Instagram Reels' },
  { to: '/admin/coupons', icon: Ticket, label: 'Coupons' },
  { to: '/admin/inventory', icon: Warehouse, label: 'Inventory' },
  { to: '/admin/change-password', icon: KeyRound, label: 'Change Password' },
  { to: '/admin/contact-messages', icon: Mail, label: 'Contact Messages' },
  { to: '/admin/order-returns', icon: RotateCcw, label: 'Return Requests' },
  { to: '/admin/product-reviews', icon: Star, label: 'Product Reviews' },
  { to: '/admin/settings', icon: Settings, label: 'Settings' },
]

export function Sidebar() {
  const {
    sidebarCollapsed,
    toggleSidebarCollapsed,
    sidebarOpen,
    setSidebarOpen,
  } = useUiStore()

  const content = (
    <aside
      className={cn(
        'flex h-full flex-col glass-panel border-r border-luxury-700/50',
        sidebarCollapsed ? 'w-[72px]' : 'w-64'
      )}
    >
      <div className="flex items-center justify-between border-b border-luxury-700/50 p-4">
        <Logo collapsed={sidebarCollapsed} showText={false} linkTo="/" />
        <button
          onClick={toggleSidebarCollapsed}
          className="hidden lg:flex rounded-lg p-1.5 text-zinc-500 hover:bg-luxury-800 hover:text-gold-700"
        >
          <ChevronLeft
            className={cn(
              'h-4 w-4 transition-transform',
              sidebarCollapsed && 'rotate-180'
            )}
          />
        </button>
        <button
          onClick={() => setSidebarOpen(false)}
          className="lg:hidden rounded-lg p-1.5 text-zinc-500 hover:bg-luxury-800"
        >
          <X className="h-5 w-5" />
        </button>
      </div>

      <nav className="flex-1 space-y-1 overflow-y-auto p-3">
        {navItems.map(({ to, icon: Icon, label, end }) => (
          <NavLink
            key={to}
            to={to}
            end={end}
            onClick={() => setSidebarOpen(false)}
            className={({ isActive }) =>
              cn(
                'flex items-center gap-3 rounded-xl px-3 py-2.5 text-sm font-medium transition-all',
                isActive ? 'admin-nav-active' : 'admin-nav-idle'
              )
            }
          >
            <Icon className="h-5 w-5 shrink-0" />
            {!sidebarCollapsed && <span>{label}</span>}
          </NavLink>
        ))}
      </nav>

      <div className="border-t border-luxury-700/50 p-4">
        {!sidebarCollapsed && (
          <p className="text-[10px] text-zinc-600 text-center">
            © 2025 Ooudh Kraft
          </p>
        )}
      </div>
    </aside>
  )

  return (
    <>
      <div className="hidden lg:block h-screen sticky top-0 shrink-0">
        {content}
      </div>

      <AnimatePresence>
        {sidebarOpen && (
          <>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 z-40 bg-black/60 lg:hidden"
              onClick={() => setSidebarOpen(false)}
            />
            <motion.div
              initial={{ x: -280 }}
              animate={{ x: 0 }}
              exit={{ x: -280 }}
              transition={{ type: 'spring', damping: 25 }}
              className="fixed inset-y-0 left-0 z-50 lg:hidden"
            >
              {content}
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </>
  )
}
